import { useStreamingTypewriter } from '@/hooks/useStreamingTypewriter';
import { memo, useEffect, useRef, useState } from 'react';
import { MarkdownMessageContent } from './markdown-message-content';

interface StreamingMessageContentProps {
  targetText: string;
  isActive: boolean;
  onDisplayUpdate?: () => void;
  onFirstDisplay?: () => void;
}

export const StreamingMessageContent = memo(function StreamingMessageContent({
  targetText,
  isActive,
  onDisplayUpdate,
  onFirstDisplay,
}: StreamingMessageContentProps) {
  const { displayedText, setTarget, reset } = useStreamingTypewriter(isActive);
  const [markdownText, setMarkdownText] = useState('');
  const hasNotifiedFirstDisplayRef = useRef(false);
  const markdownRafRef = useRef<number | null>(null);

  useEffect(() => {
    setTarget(targetText);
  }, [setTarget, targetText]);

  useEffect(() => {
    if (!isActive) {
      reset();
      setMarkdownText('');
      hasNotifiedFirstDisplayRef.current = false;
    }
  }, [isActive, reset]);

  useEffect(() => {
    if (!displayedText) {
      setMarkdownText('');
      return;
    }

    if (!hasNotifiedFirstDisplayRef.current) {
      setMarkdownText(displayedText);
      return;
    }

    if (markdownRafRef.current !== null) {
      cancelAnimationFrame(markdownRafRef.current);
    }

    markdownRafRef.current = requestAnimationFrame(() => {
      markdownRafRef.current = null;
      setMarkdownText(displayedText);
    });

    return () => {
      if (markdownRafRef.current !== null) {
        cancelAnimationFrame(markdownRafRef.current);
        markdownRafRef.current = null;
      }
    };
  }, [displayedText]);

  useEffect(() => {
    if (!isActive || !displayedText) return;

    if (!hasNotifiedFirstDisplayRef.current) {
      hasNotifiedFirstDisplayRef.current = true;
      onFirstDisplay?.();
    }

    onDisplayUpdate?.();
  }, [displayedText, isActive, onDisplayUpdate, onFirstDisplay]);

  if (!markdownText) {
    return null;
  }

  return <MarkdownMessageContent text={markdownText} isUser={false} />;
});
